/**
 * The scan autopilot, as the Skannid tab's engine strip: one switch, when it
 * last ran and runs next, "run now", and the numbers, settings and run log
 * behind the gear.
 *
 * The switch writes straight through, because a stop button with a save step is
 * not a stop button. The strip is `prospecting/engines/EngineLine.tsx`, shared
 * with the brain autopilot and the lead watch; this file hands it the scan
 * autopilot's words and what the gear opens. It was a full card with four
 * number tiles until plan step 5, and before that it sat above the call queue
 * (plan step 3 moved it).
 *
 * The file stayed in `scan-leads/` because the engine is the leads engine:
 * every site it checks is judged as a possible lead, and its client sits in
 * `lib/scanLeads.ts` beside the leads' own. The state comes from the caller
 * (`useScanAutopilot`), so whichever tab draws the strip also decides which
 * list the busy poll refreshes.
 *
 * The settings form lives in `autopilot/AutopilotSettings.tsx` and the run log
 * in `autopilot/AutopilotHistory.tsx`; they were one 300-line file, and the
 * two halves are edited for entirely different reasons.
 */
import { useEffect, useState } from "react";
import { Bot } from "lucide-react";
import { formatWhen } from "../../../lib/siteScan";
import { useSignals } from "../../shell/SignalsProvider";
import {
  noteKey,
  runAutopilot,
  saveAutopilot,
  type AutopilotSettings,
  type AutopilotState,
} from "../../../lib/scanLeads";
import { EngineLine, EngineStat } from "../prospecting/engines";
import type { T } from "../site-scanner/types";
import { AutopilotHistory } from "./autopilot/AutopilotHistory";
import { AutopilotSettings as SettingsForm } from "./autopilot/AutopilotSettings";

/** How long "Run now" keeps the spinner before re-reading the run's status. */
const START_SETTLE_MS = 1500;

export function AutopilotPanel({
  state,
  onChanged,
  canManage,
  t,
}: {
  state: AutopilotState | null;
  onChanged: () => void;
  canManage: boolean;
  t: T;
}) {
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const { refresh: refreshSignals } = useSignals();

  // The rail's jobs card is fed by a 60-second poll, and a manual batch of ten
  // sites is over in twelve seconds - so the one place in the interface that
  // exists to say "something is running in the background" reliably missed the
  // whole run. Every time this strip learns the autopilot started or stopped,
  // the rail is told as well.
  const busy = Boolean(state?.busy);
  useEffect(() => {
    refreshSignals();
  }, [busy, refreshSignals]);

  if (!state) return null;
  const s = state.settings;
  // Guarded: one 200 response with a thinner body than expected used to throw
  // here and take the whole page down with it, the same way the sidebar signals
  // did before they were fixed.
  const last = state.runs?.[0] ?? null;
  const runs = state.runs ?? [];

  const patch = async (change: Partial<AutopilotSettings>) => {
    setSaving(true);
    try {
      await saveAutopilot(change);
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      await runAutopilot(10);
      onChanged();
      // The POST answers before the batch has picked up its first site, so the
      // refresh above can still read `busy: false` and the strip sits there
      // looking as though the button did nothing. One delayed follow-up hands
      // the job over to the busy poll in `useScanAutopilot`; a batch that finished
      // instantly because it had nothing to do is already on the strip by then,
      // with its reason.
      await new Promise((resolve) => setTimeout(resolve, START_SETTLE_MS));
      onChanged();
      refreshSignals();
    } finally {
      setStarting(false);
    }
  };

  // The last run in one line. When it did nothing, the reason replaces the
  // counters: "checked 0, added 0, dropped 0" is a riddle, and the answer ("the
  // register has nothing left to scan") is already known here.
  const lastRun = last
    ? last.note
      ? `${t("leads.autopilot.lastRunIdle", { when: formatWhen(last.startedAt) })} ${t(
          `leads.autopilot.note.${noteKey(last.note)}`,
        )}`
      : t("leads.autopilot.lastRun", {
          when: formatWhen(last.startedAt),
          checked: last.checked,
          added: last.added,
          dropped: last.dropped,
        })
    : t("leads.autopilot.neverRan");
  const when = state.nextRunAt
    ? `${lastRun} · ${t("leads.autopilot.nextRun", { when: formatWhen(state.nextRunAt) })}`
    : lastRun;

  return (
    <EngineLine
      icon={Bot}
      title={t("leads.autopilot.title")}
      status={
        state.busy
          ? t("leads.autopilot.running")
          : s.enabled
            ? t("leads.autopilot.on", { minutes: s.intervalMinutes })
            : t("leads.autopilot.off")
      }
      when={when}
      enabled={s.enabled}
      busy={state.busy}
      canManage={canManage}
      saving={saving}
      onToggle={(checked) => void patch({ enabled: checked })}
      run={{
        label: t(state.busy ? "leads.autopilot.busy" : "leads.autopilot.runNow"),
        pending: starting || state.busy,
        onRun: () => void start(),
      }}
      settingsLabel={t("leads.autopilot.settings")}
    >
      <div className="crm-pstats pt-3">
        <EngineStat label={t("leads.autopilot.stat.scans")} value={state.stats.scans} />
        <EngineStat label={t("leads.autopilot.stat.due")} value={state.stats.due} />
        <EngineStat label={t("leads.autopilot.stat.leads")} value={state.stats.leads} />
        <EngineStat label={t("leads.autopilot.stat.new")} value={state.stats.newLeads} hot />
      </div>
      <SettingsForm settings={s} canManage={canManage} saving={saving} onPatch={(c) => void patch(c)} t={t} />
      <AutopilotHistory runs={runs} t={t} />
    </EngineLine>
  );
}
